/**
 * Upload Queue Repository - Supabase storage operations for file uploads.
 *
 * Handles:
 * - File upload to Supabase storage
 * - Upload verification (size matching)
 * - Storage metadata queries
 */

import { supabase } from "@/integrations/supabase/client";
import { readSupabaseOverride } from "@/lib/chatbotBaseUrl";
import * as tus from "tus-js-client";
import { resolveContentType } from "@/domains/documents/constants/fileTypes";
import { storageQuotaErrorMessage } from "@/domains/documents/services/buildingStorageQuota";

/**
 * Clear all TUS fingerprints for a specific file from localStorage.
 * The fingerprint format is: tus::tus-br-{name}-{type}-{size}-{lastModified}-{endpoint}::{id}
 * This prevents stale fingerprints from causing infinite HEAD request loops.
 */
function clearTusFingerprints(file: File, endpoint: string): void {
  // Build the exact fingerprint prefix for this specific file
  // TUS fingerprint format: tus::tus-br-{name}-{type}-{size}-{lastModified}-{endpoint}::{id}
  const fingerprintPrefix = `tus::tus-br-${file.name}-${file.type}-${file.size}-${file.lastModified}-${endpoint}`;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("tus::")) {
      // Only remove fingerprints that match the exact prefix for THIS file
      // Do NOT use key.includes(file.name) - it's too broad and would match
      // unrelated files with similar names (e.g., "report.pdf" vs "final_report.pdf")
      if (key.startsWith(fingerprintPrefix)) {
        keysToRemove.push(key);
      }
    }
  }

  for (const key of keysToRemove) {
    console.log("[TUS] Removing stale fingerprint:", key);
    localStorage.removeItem(key);
  }
}

/**
 * Ensure we have a fresh (non-expired) auth token.
 *
 * @param forceRefresh - When true, forces a network call to refresh the token
 *   (use after a 401 rejection, since the cached token is known-stale).
 *   When false, returns the cached token if it has >30s remaining.
 * @returns A valid access token string
 * @throws Error if no valid session exists (user must re-authenticate)
 */
export async function ensureFreshToken(forceRefresh = false): Promise<string> {
  // After a 401 rejection the cached token is known-stale —
  // call refreshSession() to actually hit the network and get a new JWT.
  if (forceRefresh) {
    console.log("[Auth] Force-refreshing token via refreshSession()");
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session?.access_token) {
      console.error("[Auth] refreshSession() failed:", error?.message);
      throw new Error("AUTH_EXPIRED");
    }
    return data.session.access_token;
  }

  // Normal path: return the cached session if the token is still valid
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session?.access_token) {
    throw new Error("AUTH_EXPIRED");
  }

  // Proactively refresh if the token expires within the next 30 seconds.
  // This prevents handing a nearly-expired token to a TUS upload that
  // might take several seconds before the first chunk reaches the server.
  const expiresAt = session.expires_at; // Unix timestamp in seconds
  if (expiresAt) {
    const nowSec = Math.floor(Date.now() / 1000);
    if (nowSec >= expiresAt - 30) {
      console.log("[Auth] Token expiring soon, proactively refreshing");
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !data.session?.access_token) {
        throw new Error("AUTH_EXPIRED");
      }
      return data.session.access_token;
    }
  }

  return session.access_token;
}

/**
 * Result of an upload operation.
 */
export interface UploadResult {
  success: boolean;
  path?: string;
  error?: string;
  errorCode?: "STORAGE_ADMISSION";
}

/**
 * Result of upload verification.
 */
export interface VerificationResult {
  verified: boolean;
  storedSize?: number;
  expectedSize: number;
  error?: string;
}

/**
 * Callback for upload progress updates.
 */
export interface UploadProgressCallback {
  (bytesUploaded: number, bytesTotal: number): void;
}

/**
 * Upload a file to Supabase storage.
 *
 * @param file - The File object to upload
 * @param filePath - The destination path in storage
 * @param bucket - The storage bucket name (default: "documents")
 * @returns Upload result with success status and path or error
 */
export async function uploadFile(
  file: File,
  filePath: string,
  bucket = "documents"
): Promise<UploadResult> {
  try {
    const { data, error } = await supabase.storage.from(bucket).upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

    if (error) {
      const quotaError = storageQuotaErrorMessage(error.message);
      return {
        success: false,
        error: quotaError ?? error.message,
        errorCode: quotaError ? "STORAGE_ADMISSION" : undefined,
      };
    }

    return {
      success: true,
      path: data.path,
    };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * Upload a file using TUS resumable uploads with real-time progress tracking.
 * Provides actual byte-level progress and can resume interrupted uploads.
 *
 * @param file - The File object to upload
 * @param filePath - The destination path in storage
 * @param bucket - The storage bucket name (default: "documents")
 * @param onProgress - Optional callback for progress updates (bytesUploaded, bytesTotal)
 * @returns Upload result with success status and path or error
 */
export async function uploadFileWithProgress(
  file: File,
  filePath: string,
  bucket = "documents",
  onProgress?: UploadProgressCallback
): Promise<UploadResult> {
  // Get a fresh auth token (auto-refreshes if needed)
  let accessToken: string;
  try {
    accessToken = await ensureFreshToken();
  } catch {
    return {
      success: false,
      error: "AUTH_EXPIRED",
    };
  }

  // The branch switcher's override, read through the same validated reader as the Supabase
  // client — an upload carries the user's access token, so the host allowlist applies here too.
  const override = readSupabaseOverride();
  const supabaseUrl: string = override?.url ?? (import.meta.env.VITE_SUPABASE_URL as string);
  const anonKey: string = override?.anonKey ?? (import.meta.env.VITE_SUPABASE_ANON_KEY as string);

  const tusEndpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

  console.log("[TUS] Starting upload to:", tusEndpoint, "file:", file.name, "size:", file.size);

  // Clear any stale TUS fingerprints for this file before starting
  clearTusFingerprints(file, tusEndpoint);

  return new Promise((resolve) => {
    const upload = new tus.Upload(file, {
      endpoint: tusEndpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        // Never overwrite an existing object: storage objects are shared and
        // never hard-deleted; every attempt targets a fresh uuid path anyway.
        "x-upsert": "false",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      // Disable fingerprint storage - Supabase TUS URLs expire quickly
      // and stale fingerprints cause infinite HEAD request loops
      storeFingerprintForResuming: false,
      metadata: {
        bucketName: bucket,
        objectName: filePath,
        contentType: resolveContentType(file),
        cacheControl: "3600",
      },
      chunkSize: 6 * 1024 * 1024,
      onAfterResponse: (req, res) => {
        // CRITICAL: Wrap in try/catch to prevent callback errors from aborting upload.
        // tus-js-client does NOT wrap callbacks in try/catch, so any thrown error
        // will propagate up and be treated as an upload failure.
        try {
          const status = res.getStatus();
          if (status < 200 || status >= 300) {
            const quotaError = storageQuotaErrorMessage(res.getBody());
            if (quotaError) {
              upload.abort(true);
              resolve({ success: false, error: quotaError, errorCode: "STORAGE_ADMISSION" });
              return;
            }
          }
          console.log("[TUS] Response:", status, req.getMethod(), req.getURL());
          console.log("[TUS] Upload-Offset:", res.getHeader("Upload-Offset"));
          console.log("[TUS] Upload-Length:", res.getHeader("Upload-Length"));
          console.log("[TUS] Location:", res.getHeader("Location"));

          // Detect auth failures — abort and signal for token refresh + retry
          if (status === 401 || status === 403) {
            console.warn(
              "[TUS] Auth error detected (HTTP",
              status,
              "), aborting for token refresh"
            );
            upload.abort(true);
            resolve({
              success: false,
              error: "AUTH_EXPIRED",
            });
          }
        } catch (e) {
          console.error("[TUS] Error in onAfterResponse callback (ignored):", e);
        }
      },
      onError: (error) => {
        console.error("[TUS] Upload error:", error.message);
        // Also detect auth errors from the error message (e.g., tus-js-client wraps HTTP errors)
        const msg = error.message || "";
        const quotaError = storageQuotaErrorMessage(msg);
        if (quotaError) {
          resolve({ success: false, error: quotaError, errorCode: "STORAGE_ADMISSION" });
          return;
        }
        if (msg.includes("401") || msg.includes("403") || msg.includes("Unauthorized")) {
          resolve({
            success: false,
            error: "AUTH_EXPIRED",
          });
          return;
        }
        resolve({
          success: false,
          error: msg || "Upload failed",
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        // CRITICAL: Wrap in try/catch to prevent callback errors from aborting upload.
        // tus-js-client does NOT wrap callbacks in try/catch, so any thrown error
        // (e.g., MST "[mobx-state-tree] Cannot modify..." errors) will propagate up
        // and be treated as an upload failure, even if the upload actually succeeded.
        try {
          const percent = Math.round((bytesUploaded / bytesTotal) * 100);
          console.log(`[TUS] Progress: ${bytesUploaded}/${bytesTotal} (${percent}%)`);
          if (onProgress) {
            onProgress(bytesUploaded, bytesTotal);
          }
        } catch (e) {
          console.error("[TUS] Error in onProgress callback (ignored):", e);
        }
      },
      onSuccess: () => {
        resolve({
          success: true,
          path: filePath,
        });
      },
    });

    // Start fresh upload (no resume attempts)
    upload.start();
  });
}

/**
 * Verify an uploaded file by checking its stored size matches the expected size.
 * This is critical for detecting silent upload failures (0-byte files).
 *
 * @param filePath - The path to the file in storage
 * @param expectedSize - The expected file size in bytes
 * @param bucket - The storage bucket name (default: "documents")
 * @returns Verification result indicating if sizes match
 */
export async function verifyUpload(
  filePath: string,
  expectedSize: number,
  bucket = "documents"
): Promise<VerificationResult> {
  try {
    // List the file to get its metadata
    // We need to extract the folder path and filename
    const lastSlashIndex = filePath.lastIndexOf("/");
    const folderPath = lastSlashIndex > 0 ? filePath.substring(0, lastSlashIndex) : "";
    const fileName = lastSlashIndex > 0 ? filePath.substring(lastSlashIndex + 1) : filePath;

    const { data, error } = await supabase.storage.from(bucket).list(folderPath, {
      limit: 1,
      search: fileName,
    });

    if (error) {
      return {
        verified: false,
        expectedSize,
        error: `Failed to verify upload: ${error.message}`,
      };
    }

    if (!data || data.length === 0) {
      return {
        verified: false,
        expectedSize,
        error: "File not found in storage after upload",
      };
    }

    // Find the exact file (search is prefix-based)
    const fileInfo = data.find((f) => f.name === fileName);
    if (!fileInfo) {
      return {
        verified: false,
        expectedSize,
        error: "File not found in storage after upload",
      };
    }

    const storedSize = fileInfo.metadata?.size ?? 0;

    // Check if sizes match
    if (storedSize !== expectedSize) {
      return {
        verified: false,
        storedSize,
        expectedSize,
        error: `Size mismatch: expected ${expectedSize} bytes, got ${storedSize} bytes`,
      };
    }

    return {
      verified: true,
      storedSize,
      expectedSize,
    };
  } catch (e) {
    return {
      verified: false,
      expectedSize,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}
